import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckSquare, Plus, AlertTriangle, PlayCircle, CheckCircle2, Lock, RotateCcw, UserPlus } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TaskForm from "@/components/tasks/TaskForm";
import TaskReopenDialog from "@/components/operations/TaskReopenDialog";
import { PriorityBadge, StatusBadge, OverdueBadge, formatDue } from "@/components/tasks/TaskBadges";

// FIELD-001B3A · Canonical status transitions the Engineer surface
// exposes. `complete` is terminal from this page — Ops manages resets.
const NEXT_CANONICAL_STATUS = { not_started: "in_progress", in_progress: "complete" };
const CANONICAL_ACTION_LABEL = { not_started: "Start task", in_progress: "Mark complete" };

/**
 * ProjectTasksSection — the "Tasks" panel embedded on the Project Detail page.
 * Shows open / completed / overdue counters and an inline task list scoped to
 * a single project. Includes quick-create + template-apply.
 *
 * FIELD-001B · When the backend response carries `workflow_tasks`, we render
 * them alongside ad-hoc tasks with a "Workflow" pill so the Engineer sees
 * a single unified view. New task and Templates controls are gated on
 * `manage_projects` — Engineers can never invoke them.
 */
export default function ProjectTasksSection({ projectId, projects, engineers, leadEngineerId = null, myEngineerId = null, testid = "project-tasks-section" }) {
    const { user } = useAuth();
    const canManage = Array.isArray(user?.permissions)
        && user.permissions.includes("manage_projects");
    // FIELD-001C · Project Lead detection is server-authoritative
    // (`projects.lead_engineer_id`). The parent hands us both the
    // authoritative lead id and the caller's mapped engineer id so
    // we can gate New task + canonical Lead-assign controls without
    // pinging the Ops-only roster endpoint.
    const isLead = !!leadEngineerId && !!myEngineerId && leadEngineerId === myEngineerId;
    const canCreateTasks = canManage || isLead;

    // FIELD-001C · When the parent didn't load a full roster (Lead
    // users can't read the Ops-only `/engineers` endpoint), fall back
    // to the project-scoped assignment-options endpoint so the New
    // task form is never showing "No active resources" for permitted
    // callers. Ops uses whichever list arrives first.
    const [resolvedEngineers, setResolvedEngineers] = useState(Array.isArray(engineers) ? engineers : []);
    const [engineersLoadError, setEngineersLoadError] = useState(false);
    useEffect(() => {
        if (Array.isArray(engineers) && engineers.length > 0) {
            setResolvedEngineers(engineers);
            setEngineersLoadError(false);
            return;
        }
        if (!canCreateTasks || !projectId) return;
        let cancelled = false;
        (async () => {
            try {
                setEngineersLoadError(false);
                const { options } = await api.projectAssignmentOptions(projectId);
                if (!cancelled) setResolvedEngineers(options || []);
            } catch {
                if (!cancelled) setEngineersLoadError(true);
            }
        })();
        return () => { cancelled = true; };
    }, [engineers, canCreateTasks, projectId]);
    const [data, setData] = useState(null);
    const [error, setError] = useState("");
    const [metadata, setMetadata] = useState({ categories: [], priorities: [], statuses: [] });
    const [templates, setTemplates] = useState([]);
    const [formOpen, setFormOpen] = useState(false);
    const [formInitial, setFormInitial] = useState(null);
    const [showTemplates, setShowTemplates] = useState(false);
    // FIELD-001C2B · Ops-only ad-hoc reopen dialog state.
    const [reopenTask, setReopenTask] = useState(null);

    const load = useCallback(async () => {
        try {
            const d = await api.projectTasks(projectId);
            setData(d);
            setError("");
        } catch (e) {
            setError(formatApiError(e?.response?.data?.detail) || "Failed to load tasks");
        }
    }, [projectId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        if (!canCreateTasks) return;
        (async () => {
            try {
                const [md, tmpls] = await Promise.all([api.taskMetadata(), canManage ? api.listTaskTemplates() : Promise.resolve([])]);
                setMetadata(md); setTemplates(tmpls);
            } catch { /* best-effort */ }
        })();
    }, [canCreateTasks, canManage]);

    const workflowTasks = useMemo(() => data?.workflow_tasks || [], [data]);
    const adHocTasks = useMemo(() => data?.ad_hoc_tasks || (Array.isArray(data?.tasks) ? data.tasks.filter((t) => t.kind !== "workflow") : []), [data]);

    async function applyTemplate(tid) {
        try {
            const r = await api.createFromTemplate({ project_id: projectId, template_id: tid });
            toast.success(`Applied ${r.template_name} — ${r.tasks.length} tasks`);
            setShowTemplates(false);
            await load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Template failed");
        }
    }

    async function toggleComplete(t) {
        // FIELD-001C2B · Ad-hoc completed rows are read-only for field
        // users and locked from generic PATCH — Ops reopens via the
        // dedicated dialog with a reason (mirrors the canonical rule).
        if (t.status === "Completed") {
            toast.error("Completed tasks must be reopened with a reason.");
            return;
        }
        try {
            await api.updateTask(t.id, { status: "Completed" });
            await load();
        } catch (e) {
            toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
        }
    }

    // FIELD-001B3A · Canonical workflow task transition. Uses the same
    // PATCH endpoint the phases accordion previously called, but
    // targets the row that is actually visible on the Engineer page
    // (`ProjectTasksSection`). Refuses locally on Blocked so we never
    // dispatch a request the backend will refuse; the PATCH endpoint
    // remains authoritative for assignment + dependency enforcement.
    async function cycleWorkflowTask(t) {
        const raw = t.canonical_status;
        const next = NEXT_CANONICAL_STATUS[raw];
        if (!next) return;
        if (t.is_blocked) {
            toast.error("Blocked — complete prerequisite tasks first.");
            return;
        }
        try {
            await api.updatePhaseTaskStatus(t.project_id, t.canonical_task_id, next);
            toast.success(raw === "not_started" ? "Task started" : "Task completed");
            await load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            if (d?.code === "TASK_NOT_ASSIGNED_TO_YOU") {
                toast.error("You are not assigned to this task.");
            } else if (d?.code === "TASK_DEPENDENCY_UNMET") {
                toast.error(d?.message || "Blocked by unmet prerequisites.");
            } else {
                toast.error(formatApiError(d) || "Could not update task");
            }
        }
    }

    // FIELD-001C · Canonical Assign-to-me (Ops or Project Lead).
    // Delegates to the authoritative
    // `POST /projects/{id}/tasks/{tid}/assign-to-me` endpoint which
    // enforces:
    //   • caller maps to an active engineer roster row
    //   • authorised as Ops or the Project Lead
    //   • atomic still-unassigned + incomplete guard
    //   • required_resource_type mismatch surfaces as 409
    // A forged claim by an unauthorised user remains refused server-side.
    async function claimCanonicalTask(t) {
        try {
            await api.assignCanonicalTaskToMe(t.project_id, t.canonical_task_id);
            toast.success("Assigned to you");
            await load();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error((typeof d === "object" ? d?.message : null)
                || formatApiError(d)
                || "Couldn't assign.");
        }
    }

    return (
        <div className="pe-card p-5 md:p-6" data-testid={testid}>
            <div className="flex items-start gap-2 mb-3">
                <CheckSquare size={16} strokeWidth={1.75} className="text-[#3E9E2E] mt-0.5" />
                <div className="flex-1 min-w-0">
                    <h3 className="text-base md:text-lg text-[#131B2F] font-medium">Tasks</h3>
                    {data && (
                        <div className="text-xs text-[#4B5565] mt-0.5" data-testid="project-tasks-counters">
                            {data.open_count} open · {data.overdue_count} overdue · {data.completed_count} completed
                        </div>
                    )}
                </div>
                <div className="relative">
                    {canManage && (
                    <button
                        onClick={() => setShowTemplates((v) => !v)}
                        data-testid="project-tasks-template-btn"
                        className="text-sm text-[#131B2F] border border-[#D1D5DB] rounded-md px-2.5 py-1.5 hover:bg-[#F5F5F7] font-medium"
                    >
                        Templates
                    </button>
                    )}
                    {showTemplates && canManage && (
                        <div className="absolute right-0 top-10 z-20 w-72 bg-white border border-[#E5E7EB] rounded-md shadow-lg" data-testid="project-tasks-template-menu">
                            {templates.map((t) => (
                                <button
                                    key={t.id}
                                    onClick={() => applyTemplate(t.id)}
                                    data-testid={`project-tasks-template-${t.id}`}
                                    className="w-full text-left px-3 py-2.5 hover:bg-[#F5F5F7] border-b border-[#F3F4F6] last:border-b-0"
                                >
                                    <div className="text-sm font-medium text-[#131B2F]">{t.name} · {t.task_count} tasks</div>
                                    <div className="text-xs text-[#4B5565]">{t.description}</div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                {canCreateTasks && (
                <button
                    onClick={() => { setFormInitial(null); setFormOpen(true); }}
                    data-testid="project-tasks-new"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md bg-[#3E9E2E] text-white text-sm font-medium hover:bg-[#348426]"
                >
                    <Plus size={13} strokeWidth={2} /> New
                </button>
                )}
            </div>

            {error && <div className="text-sm text-[#DA3535]">{error}</div>}
            {!data ? (
                <div className="text-sm text-[#4B5565] py-4">Loading tasks…</div>
            ) : (adHocTasks.length + workflowTasks.length) === 0 ? (
                <div className="text-sm text-[#4B5565] text-center py-6 border border-dashed border-[#D1D5DB] rounded-lg" data-testid="project-tasks-empty">
                    {canManage
                        ? "No tasks for this project yet. Apply a template or create one."
                        : "No workflow tasks assigned to you on this project yet."}
                </div>
            ) : (
                <ul className="divide-y divide-[#F3F4F6]" data-testid="project-tasks-list">
                    {workflowTasks.map((t) => {
                        const raw = t.canonical_status;
                        // FIELD-001C · Backend now emits two shapes of
                        // canonical row to a Project Lead:
                        //   1. Assigned to me     → `assigned_engineer_id` matches; action controls fire.
                        //   2. Unassigned + lead  → `assigned_engineer_id` is null; Assign-to-me control fires.
                        // Any other canonical row indicates a task
                        // owned by someone else — surface "Not
                        // assigned to you" and no controls.
                        const assignedToMe = !!t.assigned_engineer_id;
                        const isUnassigned = !t.assigned_engineer_id;
                        const isComplete = raw === "complete" || t.status === "Completed";
                        const isBlocked = !!t.is_blocked && !isComplete;
                        const blockingNames = Array.isArray(t.blocking_task_names) ? t.blocking_task_names : [];
                        const actionLabel = CANONICAL_ACTION_LABEL[raw];
                        const showAction = assignedToMe && !isComplete && !isBlocked && !!actionLabel;
                        // Assign-to-me is offered to anyone the backend
                        // considers a Project Lead of this project OR
                        // to Operations (server enforces independently).
                        // Ordinary Field Users never receive an
                        // unassigned canonical row — so gating on
                        // `canCreateTasks` is a belt-and-braces UI
                        // check that mirrors the server predicate.
                        const showClaim = isUnassigned && !isComplete && !isBlocked && canCreateTasks;
                        return (
                            <li key={t.id}
                                data-testid={`project-task-row-${t.id}`}
                                data-assigned-to-me={assignedToMe ? "true" : undefined}
                                data-unassigned={isUnassigned ? "true" : undefined}
                                data-blocked={isBlocked ? "true" : undefined}
                                className="py-2.5 flex items-start gap-3">
                                <span className={`mt-1 w-4 h-4 rounded-full border ${isComplete ? "bg-[#3E9E2E] border-[#3E9E2E]" : "bg-white border-[#D1D5DB]"}`} />
                                <div className="flex-1 min-w-0">
                                    <div className={`text-sm font-medium ${isComplete ? "line-through text-[#4B5565]" : "text-[#131B2F]"}`}>
                                        {t.title}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-1.5 mt-1 text-xs">
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#EEF2FF] text-[#3730A3]"
                                              data-testid={`project-task-kind-workflow-${t.id}`}>
                                            Workflow
                                        </span>
                                        <StatusBadge status={t.status} small />
                                        <span className="text-[#4B5565]">{t.phase_name || "Workflow"}</span>
                                        {t.required_resource_type && (
                                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#EEF2FF] text-[#3730A3]"
                                                  data-testid={`project-task-type-${t.id}`}>
                                                {t.required_resource_type}
                                            </span>
                                        )}
                                        {isBlocked && (
                                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#FEECEC] text-[#B45309]"
                                                  data-testid={`project-task-blocked-${t.id}`}>
                                                <Lock size={10} strokeWidth={2.25} /> Blocked
                                            </span>
                                        )}
                                    </div>
                                    {isBlocked && blockingNames.length > 0 && (
                                        <div className="mt-1 text-[11px] text-[#B45309]"
                                             data-testid={`project-task-blocked-reason-${t.id}`}>
                                            Waiting on: {blockingNames.join(", ")}
                                        </div>
                                    )}
                                </div>
                                {showAction ? (
                                    <button
                                        type="button"
                                        onClick={() => cycleWorkflowTask(t)}
                                        data-testid={`project-task-action-${t.id}`}
                                        data-action={raw === "not_started" ? "start" : "complete"}
                                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                            raw === "in_progress"
                                                ? "bg-[#3E9E2E] text-white hover:bg-[#348426]"
                                                : "bg-[#131B2F] text-white hover:bg-[#0B1220]"
                                        }`}
                                    >
                                        {raw === "in_progress"
                                            ? <CheckCircle2 size={12} strokeWidth={2} />
                                            : <PlayCircle    size={12} strokeWidth={2} />}
                                        {actionLabel}
                                    </button>
                                ) : showClaim ? (
                                    <button
                                        type="button"
                                        onClick={() => claimCanonicalTask(t)}
                                        data-testid={`project-task-claim-${t.id}`}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-[#D1D5DB] bg-white text-[#131B2F] hover:bg-[#F5F5F7]"
                                    >
                                        <UserPlus size={11} strokeWidth={2} /> Assign to me
                                    </button>
                                ) : (!assignedToMe && (
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-[#F5F5F7] text-[#4B5565]"
                                          data-testid={`project-task-not-assigned-${t.id}`}>
                                        Not assigned to you
                                    </span>
                                ))}
                            </li>
                        );
                    })}
                    {adHocTasks.map((t) => {
                        const names = Array.isArray(t.assigned_engineer_names) && t.assigned_engineer_names.length
                            ? t.assigned_engineer_names.filter(Boolean)
                            : (t.assigned_engineer_name ? [t.assigned_engineer_name] : []);
                        const isCompleted = t.status === "Completed";
                        return (
                        <li key={t.id} className={`py-2.5 flex items-start gap-3 ${t.is_overdue ? "bg-[#FEF3F3]/40 rounded-md -mx-2 px-2" : ""}`} data-testid={`project-task-row-${t.id}`}>
                            <input
                                type="checkbox"
                                checked={isCompleted}
                                onChange={() => toggleComplete(t)}
                                disabled={isCompleted}
                                data-testid={`project-task-check-${t.id}`}
                                className="mt-1"
                                aria-label={`Mark ${t.title} complete`}
                            />
                            <div className="flex-1 min-w-0">
                                <button
                                    onClick={() => { setFormInitial(t); setFormOpen(true); }}
                                    data-testid={`project-task-open-${t.id}`}
                                    className={`text-left text-sm font-medium ${isCompleted ? "line-through text-[#4B5565]" : "text-[#131B2F]"}`}
                                >
                                    {t.title}
                                </button>
                                <div className="flex flex-wrap items-center gap-1.5 mt-1 text-xs">
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#F5F5F7] text-[#4B5565]"
                                          data-testid={`project-task-kind-ad-hoc-${t.id}`}>
                                        Ad-hoc
                                    </span>
                                    <PriorityBadge priority={t.priority} small />
                                    <StatusBadge status={t.status} small />
                                    {t.is_overdue && <OverdueBadge small />}
                                    {t.required_resource_type && (
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-[#EEF2FF] text-[#3730A3]"
                                              data-testid={`project-task-type-${t.id}`}>
                                            {t.required_resource_type}
                                        </span>
                                    )}
                                    <span className="text-[#4B5565]" data-testid={`project-task-assignees-${t.id}`}>
                                        {t.task_category}
                                        {names.length
                                            ? ` · ${names.join(", ")}`
                                            : (isCompleted ? "" : " · Unassigned")}
                                    </span>
                                    {formatDue(t) && (
                                        <span className="text-[#4B5565]">· Due {formatDue(t)}</span>
                                    )}
                                </div>
                            </div>
                            {canManage && isCompleted && (
                                <button
                                    type="button"
                                    onClick={() => setReopenTask(t)}
                                    data-testid={`project-task-reopen-${t.id}`}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-[#D1D5DB] bg-white text-[#131B2F] hover:bg-[#F5F5F7]"
                                >
                                    <RotateCcw size={11} strokeWidth={2} /> Reopen
                                </button>
                            )}
                            {!isCompleted && names.length === 0 && (
                                <button
                                    type="button"
                                    data-testid={`project-task-assign-to-me-${t.id}`}
                                    onClick={async () => {
                                        try {
                                            await api.assignTaskToMe(t.id);
                                            toast.success("Assigned to you");
                                            await load();
                                        } catch (e) {
                                            const d = e?.response?.data?.detail;
                                            toast.error((typeof d === "object" ? d?.message : null)
                                                || formatApiError(d)
                                                || "Couldn't assign.");
                                        }
                                    }}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium border border-[#D1D5DB] bg-white text-[#131B2F] hover:bg-[#F5F5F7]"
                                >
                                    <UserPlus size={11} strokeWidth={2} /> Assign to me
                                </button>
                            )}
                        </li>
                        );
                    })}
                </ul>
            )}

            {formOpen && (
                <TaskForm
                    open
                    initial={formInitial}
                    projects={projects}
                    engineers={resolvedEngineers}
                    metadata={metadata}
                    lockedProjectId={projectId}
                    enableMultiAssign={canCreateTasks}
                    onClose={async (saved) => {
                        setFormOpen(false);
                        setFormInitial(null);
                        if (saved) await load();
                    }}
                />
            )}

            {reopenTask && (
                <TaskReopenDialog
                    variant="adhoc"
                    projectId={projectId}
                    task={{ ...reopenTask, name: reopenTask.title }}
                    onClose={() => setReopenTask(null)}
                    onReopened={async () => { setReopenTask(null); await load(); }}
                />
            )}
        </div>
    );
}
